import * as _ from 'lodash';
import { Button } from 'rbx';
import * as React from 'react';
import { useMutation } from '@apollo/client';
import { MutationFunction } from '@apollo/client';

import { plantS3Folder } from '../../models/IPlantModel';
import { PLANT_SAVE_MUTATION } from './../../api/GraphQLQueries/Plant';
import Error from './../../components/layout/Error';
import Message from './../../components/layout/Message';
import Pane from './../../components/ui/Pane';
import IPlantModel, { IPlantCreateModel } from './../../models/IPlantModel';
import { todo } from './../../utils/translate';
import PlantCreateForm from './forms/PlantCreateForm';
import FolderDetailQuery from './queries/FolderDetailQuery';

interface IProps {
    onCancel: () => void;
    onSuccess: (id: string) => void;
}

const plantTemplate: IPlantCreateModel = {
    name: '',
    id: '',
    customerDisplayName: '',
    deliveryAddress: { country: '', responsible: '', street: '', city: '', zip: '' },
    invoiceAddress: { country: '', responsible: '', street: '', city: '', zip: '' },
};

const PlantCreateContainer: React.FC<IProps> = ({ onCancel, onSuccess }) => {
    const [plant, setPlant] = React.useState<IPlantCreateModel>({ ...plantTemplate });
    const [error, setError] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [s3Folder, setS3Folder] = React.useState(false);

    const [createPlantMutation] = useMutation(PLANT_SAVE_MUTATION, {
        onError: () => {
            setError(true);
            setSuccess(false);
        },
        onCompleted: (data) => {
            setError(false);
            setSuccess(true);
            onSuccess(data.savePlant.id);
        },
    });

    const updateValues = (updatedPlant: IPlantCreateModel) => {
        setPlant(updatedPlant);
        setError(false);
        setSuccess(false);
    };

    const formSubmit = () => {
        createPlantMutation({ variables: { plant: _.omit(plant, '__typename') } });
    };

    const isPlantInValid = () => {
        return (
            !plant ||
            !plant.id ||
            plant.id.length < 3 ||
            !plant.name ||
            plant.name.length < 3 ||
            _.some(
                [
                    ..._.values(_.omit(plant.invoiceAddress, ['latitude', 'longitude'])),
                    ..._.values(_.omit(plant.deliveryAddress, ['latitude', 'longitude'])),
                ],
                _.isEmpty,
            )
        );
    };

    const renderSaveButton = (folderExists: boolean) => {
        const disabled = !folderExists || isPlantInValid();
        return (
            <Button
                onClick={() => {
                    if (!disabled) {
                        formSubmit();
                    }
                }}
                disabled={disabled}
            >
                Create plant
            </Button>
        );
    };

    const onS3Completed = (folderExists: boolean): void => {
        if (s3Folder !== folderExists) {
            setS3Folder(folderExists);
        }
    };

    const renderPlantCreateForm = () => {
        const s3FolderName = plantS3Folder(plant.id, plant.name);

        const createPlantAction = (
            <FolderDetailQuery
                onResult={onS3Completed}
                plantS3Folder={s3FolderName}
                renderFetchedFolderData={renderSaveButton}
            />
        );
        return (
            <Pane
                onClose={onCancel}
                actionButtons={createPlantAction}
                title={todo('Add plant')}
                sidepane={true}
            >
                <PlantCreateForm
                    plant={plant}
                    onUpdate={updateValues}
                    s3FolderExists={s3Folder}
                />
            </Pane>
        );
    };

    return (
        <>
            {error && <Error>Plant create error</Error>}
            {success && <Message type="success">Successfully created plant</Message>}
            {renderPlantCreateForm()}
        </>
    );
};

export default PlantCreateContainer;
